import type { Edge, Node } from '@xyflow/react';
import type {
  CauseDiagramNode,
  ConsequenceDiagramNode,
  DiagramConfiguration,
  EscalationFactorDiagramNode,
} from '../@types/diagram';
import { id as causesContainerId } from '../components/diagram/container/causes-container-node.component';
import { id as consequencesContainerId } from '../components/diagram/container/consequences-container-node.component';
import { id as mitigatingControlsContainerId } from '../components/diagram/container/mitigating-controls-container-node.component';
import { id as preventativeControlsContainerId } from '../components/diagram/container/preventative-controls-container-node.component';
import type { DiagramDisplayFilterState } from '../redux/slices/diagram';
import { DIAGRAM_MODE } from './constants';
import { EdgeDirection } from './edge-util';
import { CriticalControlEnum } from './node-util';

// spacing constants
const nodeWidth = 240, //px, defined on the base-node
  nodeHeight = 96, //px, defined on the base-node
  mueNodeWidth = 240, //px, defined on the mue-node
  mueNodeHeight = 128; //px, defined on the mue-node

const nodeStartX = 25, // node start position on X axis, relative to container
  nodeToEdgeDeltaY = 30, // desired vertical spacing between control node and edge
  nodeShiftDeltaX = 50; // horizontal shift of control nodes

const containerStartX = 0, // container start position on X axis
  containerStartY = 0, // container start position on Y axis
  containerPaddingTop = 80,
  containerPaddingBottom = 30,
  containerSpacingX = 30, // horizontal spacing between containers
  containerToMueNodeSpacingX = 120, // horizontal spacing between containers and MUE node
  baseContainerHeight = 600; // default height of a container

const rowDeltaY = 50, // vertical spacing between rows
  cellDeltaX = 50; // horizontal spacing between nodes in same row

const escalationExtraHeight = nodeHeight + rowDeltaY; // extra height to accommodate escalation factor and barrier nodes

const baseContainerWidth = nodeWidth + 2 * nodeStartX, // base width of container to contain a single node (i.e. causes container),
  nodeSpacingY = nodeHeight / 2 + nodeToEdgeDeltaY, // computed vertical spacing (accounts for node height and desired vertical spacing)
  nodeStartY = nodeHeight / 2 + nodeToEdgeDeltaY + containerPaddingTop, // node start position on Y axis, relative to container
  rowHeight = 2 * nodeHeight + 2 * nodeToEdgeDeltaY; // computed height of a row used for calculating container height

type ControlRows = Array<CauseDiagramNode | ConsequenceDiagramNode>;

type ControlSide = 'preventative' | 'mitigating';

type GeneratedControlNodeData = Record<string, unknown> & {
  recordId?: number;
  cellIndex?: number;
  handles?: Record<string, string>;
  filtered?: boolean;
};

type GeneratedControlNode = Node<GeneratedControlNodeData>;

type ControlNodeLookup = {
  byRecordId: Map<number, GeneratedControlNode>;
  byNodeId: Map<string, GeneratedControlNode>;
};

type ControlLink = {
  controlNodeId?: string;
  controlRecordId?: number;
};

const isDefined = <T>(value: T | undefined): value is T => value !== undefined;

const createFilteredDiagramConfig = (
  diagramConfig: DiagramConfiguration,
  diagramFilter?: DiagramDisplayFilterState
): DiagramConfiguration => {
  if (!diagramFilter) {
    return diagramConfig;
  }

  const enrichControls = <T extends CauseDiagramNode | ConsequenceDiagramNode>(items: Array<T>): Array<T> =>
    items.map((item) => ({
      ...item,
      controls: item.controls.map((control) => ({
        ...control,
        filtered:
          control.criticalControlType !== CriticalControlEnum.CRITICAL
            ? diagramFilter.showOnlyCriticalControls
            : control.filtered,
        showControlType: diagramFilter.showControlTypes,
        showControlEffectiveness: diagramFilter.showControlEffectiveness,
      })),
    }));

  return {
    ...diagramConfig,
    causes: enrichControls(diagramConfig.causes),
    consequences: enrichControls(diagramConfig.consequences),
  };
};

const resolveControlLayout = (index: number, showControlsInSingleRow: boolean) => {
  const mod = showControlsInSingleRow ? 0 : index % 2;
  const cell = Math.floor(showControlsInSingleRow ? index : index / 2);
  const delta = mod === 0 ? 0 : nodeShiftDeltaX;
  const isTopHandle = mod !== 0;

  return {
    mod,
    cell,
    delta,
    handleTypeKey: isTopHandle ? 'topHandleType' : 'bottomHandleType',
    handleIdKey: isTopHandle ? 'topHandleId' : 'bottomHandleId',
    handleIdValue: isTopHandle ? 'top' : 'bottom',
  };
};

const calculateEscalationXAxisAdjustment = (
  controls: Array<{ id: string; recordId?: number }>,
  index: number,
  escalationFactors: Array<EscalationFactorDiagramNode>,
  showControlsInSingleRow: boolean
) => {
  if (!showControlsInSingleRow || index === 0) {
    return 0;
  }

  const previousControl = controls[index - 1];
  const escalationFactor = escalationFactors.find((factor) => {
    if (factor.controlNodeId) {
      return factor.controlNodeId === previousControl.id;
    }
    return factor.controlRecordId === previousControl.recordId;
  });

  if (escalationFactor?.barriers && escalationFactor.barriers.length > 0) {
    return escalationFactor.barriers.length * (nodeWidth + cellDeltaX);
  }

  return 0;
};

const createCauseOrConsequenceNodes = (
  rows: ControlRows,
  parentId: string,
  nodeType: 'cause-node' | 'consequence-node',
  placeholder: 'Enter Cause' | 'Enter Consequence',
  localNodeStartY: number,
  localRowHeight: number
): Array<Node> => {
  return rows.map((item, index) => {
    const node: Node = {
      id: item.id,
      parentId,
      type: nodeType,
      position: { x: nodeStartX, y: localNodeStartY + index * (localRowHeight + rowDeltaY) },
      data: {
        recordId: item.recordId,
        linkUrl: item.linkUrl,
        label: item.label,
        rowIndex: index,
        editMode: Boolean(item.editMode),
        placeholder,
      },
    };
    return node;
  });
};

const createControlNodes = (
  rows: ControlRows,
  side: ControlSide,
  controlContainerWidth: number,
  escalationFactors: Array<EscalationFactorDiagramNode>,
  showControlsInSingleRow: boolean,
  localNodeStartY: number,
  localRowHeight: number
): Array<Array<GeneratedControlNode>> => {
  const isPreventative = side === 'preventative';
  const parentId = isPreventative ? preventativeControlsContainerId : mitigatingControlsContainerId;
  const type = isPreventative ? 'preventative-control-node' : 'mitigating-control-node';

  return rows.map((row, rowIndex) => {
    return row.controls.map((control, index) => {
      const { mod, cell, delta, handleTypeKey, handleIdKey, handleIdValue } = resolveControlLayout(
        index,
        showControlsInSingleRow
      );
      const rowNodeY = localNodeStartY + rowIndex * (localRowHeight + rowDeltaY);
      const nodeDeltaY = (mod !== 0 ? 1 : -1) * nodeSpacingY;
      const xAxisAdjustment = calculateEscalationXAxisAdjustment(
        row.controls,
        index,
        escalationFactors,
        showControlsInSingleRow
      );

      const x = isPreventative
        ? controlContainerWidth - nodeWidth - nodeStartX - cell * (nodeWidth + cellDeltaX) - delta - xAxisAdjustment
        : cell * (nodeWidth + cellDeltaX) + nodeStartX + delta + xAxisAdjustment;

      const controlNode: GeneratedControlNode = {
        id: control.id,
        parentId,
        type,
        position: {
          x,
          y: rowNodeY + nodeDeltaY,
        },
        data: {
          ...control,
          label: control.label,
          placeholder: 'Enter Control',
          cellIndex: index,
          rowIndex,
          parentRecordId: row.recordId,
          handles: {
            [handleIdKey]: handleIdValue,
            [handleTypeKey]: 'target',
          },
          mainHandle: handleIdValue,
        },
      };
      return controlNode;
    });
  });
};

const createControlNodeLookup = (controlNodes: Array<GeneratedControlNode>): ControlNodeLookup => {
  const lookup: ControlNodeLookup = {
    byRecordId: new Map<number, GeneratedControlNode>(),
    byNodeId: new Map<string, GeneratedControlNode>(),
  };

  controlNodes.forEach((controlNode) => {
    if (typeof controlNode.data.recordId === 'number') {
      lookup.byRecordId.set(controlNode.data.recordId, controlNode);
    }
    if (controlNode.id) {
      lookup.byNodeId.set(controlNode.id, controlNode);
    }
  });

  return lookup;
};

const resolveControlNode = (link: ControlLink, controlNodeLookup: ControlNodeLookup) => {
  if (link.controlNodeId) {
    return controlNodeLookup.byNodeId.get(String(link.controlNodeId));
  }

  return controlNodeLookup.byRecordId.get(link.controlRecordId ?? -1);
};

const resolveEscalationPositionData = (controlNode: GeneratedControlNode, showControlsInSingleRow: boolean) => {
  const isPreventative = controlNode.type === 'preventative-control-node';
  const modY = showControlsInSingleRow ? 0 : (controlNode.data.cellIndex ?? 0) % 2;
  const nodeDeltaY = (modY !== 0 ? 1 : -1) * (nodeHeight + rowDeltaY);

  return { isPreventative, modY, nodeDeltaY };
};

const createEscalationFactorBarrierNodes = (
  escalationFactors: Array<EscalationFactorDiagramNode>,
  controlNodeLookup: ControlNodeLookup,
  showControlsInSingleRow: boolean
): Array<Node> => {
  return escalationFactors.flatMap((escalationFactor) => {
    return (escalationFactor.barriers ?? [])
      .map((barrier, index) => {
        const controlNode = resolveControlNode(barrier, controlNodeLookup);

        if (!controlNode) {
          return;
        }

        const { isPreventative, modY, nodeDeltaY } = resolveEscalationPositionData(
          controlNode,
          showControlsInSingleRow
        );
        const nodeDeltaX = (isPreventative ? -1 : 1) * index * (nodeWidth + cellDeltaX);

        let handles: Record<string, string> = isPreventative
          ? {
              leftHandleId: 'left',
              leftHandleType: 'target',
            }
          : {
              rightHandleId: 'right',
              rightHandleType: 'target',
            };

        if (index > 0) {
          handles = isPreventative
            ? {
                ...handles,
                rightHandleId: 'right',
                rightHandleType: 'source',
              }
            : {
                ...handles,
                leftHandleId: 'left',
                leftHandleType: 'source',
              };
        }

        if (index === 0) {
          handles =
            modY !== 0
              ? {
                  ...handles,
                  topHandleId: 'top',
                  topHandleType: 'source',
                }
              : {
                  ...handles,
                  bottomHandleId: 'bottom',
                  bottomHandleType: 'source',
                };
        }

        const barrierNode: Node = {
          id: barrier.id,
          parentId: controlNode.parentId,
          type: 'escalation-factor-barrier-node',
          position: {
            x: controlNode.position.x + nodeDeltaX,
            y: controlNode.position.y + nodeDeltaY,
          },
          data: {
            ...barrier,
            label: barrier.label,
            placeholder: 'Enter Barrier',
            controlRecordId: controlNode.data.recordId,
            controlNodeId: controlNode.id,
            escalationFactorNodeId: escalationFactor.id,
            escalationFactorRecordId: escalationFactor.recordId,
            handles: {
              ...handles,
            },
            filtered: controlNode.data.filtered,
          },
        };
        return barrierNode;
      })
      .filter(isDefined);
  });
};

const createEscalationFactorNodes = (
  escalationFactors: Array<EscalationFactorDiagramNode>,
  controlNodeLookup: ControlNodeLookup,
  showControlsInSingleRow: boolean
): Array<Node> => {
  return escalationFactors
    .map((escalationFactor) => {
      const controlNode = resolveControlNode(escalationFactor, controlNodeLookup);

      if (!controlNode) {
        return;
      }

      const { isPreventative, modY, nodeDeltaY } = resolveEscalationPositionData(controlNode, showControlsInSingleRow);
      const totalBarriers = escalationFactor.barriers ? escalationFactor.barriers.length : 0;
      const nodeDeltaX = (isPreventative ? -1 : 1) * totalBarriers * (nodeWidth + cellDeltaX);

      let handleTypeKey;
      let handleIdKey;
      let handleIdValue;
      if (totalBarriers === 0) {
        const isBottom = modY !== 0;
        handleTypeKey = isBottom ? 'topHandleType' : 'bottomHandleType';
        handleIdKey = isBottom ? 'topHandleId' : 'bottomHandleId';
        handleIdValue = isBottom ? 'top' : 'bottom';
      } else {
        handleTypeKey = isPreventative ? 'rightHandleType' : 'leftHandleType';
        handleIdKey = isPreventative ? 'rightHandleId' : 'leftHandleId';
        handleIdValue = isPreventative ? 'right' : 'left';
      }

      if (controlNode.data.handles) {
        const extraHandleIdKey = modY !== 0 ? 'bottomHandleId' : 'topHandleId';
        const extraHandleTypeKey = modY !== 0 ? 'bottomHandleType' : 'topHandleType';
        const extraHandleIdValue = modY !== 0 ? 'bottom' : 'top';
        controlNode.data.handles = {
          ...controlNode.data.handles,
          [extraHandleIdKey]: extraHandleIdValue,
          [extraHandleTypeKey]: 'target',
        };
      }

      const escalationFactorNode: Node = {
        id: escalationFactor.id,
        parentId: controlNode.parentId,
        type: 'escalation-factor-node',
        position: {
          x: controlNode.position.x + nodeDeltaX,
          y: controlNode.position.y + nodeDeltaY,
        },
        data: {
          ...escalationFactor,
          label: escalationFactor.label,
          placeholder: 'Enter Escalation Factor',
          cellIndex: controlNode.data.cellIndex,
          controlRecordId: controlNode.data.recordId,
          controlNodeId: controlNode.id,
          handles: {
            [handleIdKey]: handleIdValue,
            [handleTypeKey]: 'source',
          },
          filtered: controlNode.data.filtered,
        },
      };
      return escalationFactorNode;
    })
    .filter(isDefined);
};

const createBasePathEdges = (
  sourceNodes: Array<Node>,
  controlRows: Array<Array<Node>>,
  sourceHandle: 'left' | 'right',
  targetHandle: 'left' | 'right',
  direction: EdgeDirection,
  containerId: string,
  mueId: string,
  diagramMode: DIAGRAM_MODE
) => {
  return sourceNodes.map((sourceNode, index) => {
    const controlNodeIds = controlRows[index].map((controlNode) => controlNode.id);
    return createEdge(sourceNode.id, mueId, sourceHandle, targetHandle, 'base-edge', {
      direction,
      containerId,
      controls: controlNodeIds,
      rowIndex: index,
      parentRecordId: sourceNode.data.recordId,
      diagramMode,
    });
  });
};

const createEscalationFactorEdges = (
  escalationFactorNodes: Array<Node>,
  escalationFactorBarrierNodes: Array<Node>,
  controlNodeLookup: ControlNodeLookup,
  showControlsInSingleRow: boolean
) => {
  const escalationFactorEdges: Array<Edge> = [];

  escalationFactorNodes.forEach((escalationFactorNode) => {
    const controlNode = resolveControlNode(escalationFactorNode.data as ControlLink, controlNodeLookup);

    if (!controlNode) {
      return;
    }

    const { isPreventative, modY } = resolveEscalationPositionData(controlNode, showControlsInSingleRow);
    const barrierNodes = escalationFactorBarrierNodes.filter((barrierNode) => {
      if (escalationFactorNode.data.controlNodeId) {
        return barrierNode.data.controlNodeId === escalationFactorNode.data.controlNodeId;
      }

      if (escalationFactorNode.data.controlRecordId) {
        return barrierNode.data.controlRecordId === escalationFactorNode.data.controlRecordId;
      }

      return false;
    });

    if (barrierNodes.length === 0) {
      escalationFactorEdges.push(
        createEdge(
          escalationFactorNode.id,
          controlNode.id,
          modY === 0 ? 'bottom' : 'top',
          modY === 0 ? 'top' : 'bottom',
          'straight',
          {}
        )
      );
      return;
    }

    const lastBarrierNode = barrierNodes[barrierNodes.length - 1];
    escalationFactorEdges.push(
      createEdge(
        escalationFactorNode.id,
        lastBarrierNode.id,
        isPreventative ? 'right' : 'left',
        isPreventative ? 'left' : 'right',
        'straight',
        {}
      )
    );

    for (let i = barrierNodes.length - 1; i > 0; i--) {
      const fromNode = barrierNodes[i];
      const toNode = barrierNodes[i - 1];
      escalationFactorEdges.push(
        createEdge(
          fromNode.id,
          toNode.id,
          isPreventative ? 'right' : 'left',
          isPreventative ? 'left' : 'right',
          'straight',
          {}
        )
      );
    }

    const firstBarrierNode = barrierNodes[0];
    escalationFactorEdges.push(
      createEdge(
        firstBarrierNode.id,
        controlNode.id,
        modY === 0 ? 'bottom' : 'top',
        modY === 0 ? 'top' : 'bottom',
        'straight',
        {}
      )
    );
  });

  return escalationFactorEdges;
};

/**
 * Generates nodes and edges configuration for a bow-tie diagram based on the provided diagram configuration.
 *
 * @param data - The diagram configuration containing causes, consequences, MUE (risk scenario), and hazard information
 *
 * @returns An object containing arrays of nodes and edges configured for rendering the bow-tie diagram
 * - nodes: Array of Node objects representing different elements like causes, consequences, controls, and containers
 * - edges: Array of Edge objects representing connections between nodes
 *
 * @remarks
 * The function performs the following:
 * 1. Calculates container sizes based on input data
 * 2. Creates container nodes for causes, consequences, and controls
 * 3. Generates individual nodes for causes, consequences, preventative controls, and mitigating controls
 * 4. Creates central MUE and hazard nodes
 * 5. Establishes edges between nodes to form the bowtie structure
 *
 * @example
 * ```typescript
 * const diagramConfig = {
 *   causes: [...],
 *   consequences: [...],
 *   mue: { id: 'mue1', label: 'Risk Scenario' },
 *   hazard: { id: 'hazard1', label: 'Hazard' }
 * };
 * const { nodes, edges } = generateNodesAndEdges(diagramConfig);
 * ```
 *
 * @typeParam DiagramConfiguration - The input configuration type containing the bow-tie diagram structure
 * @typeParam Node - The node configuration type for the diagram
 * @typeParam Edge - The edge configuration type for the diagram
 */

export const generateNodesAndEdges = (
  diagramConfig: DiagramConfiguration,
  filter?: DiagramDisplayFilterState,
  diagramMode: DIAGRAM_MODE = DIAGRAM_MODE.BOWTIE
) => {
  const {
    mue,
    hazard,
    causes,
    consequences,
    escalationFactors = [],
  } = createFilteredDiagramConfig(diagramConfig, filter);
  const { showControlsInSingleRow = false } = filter ?? {};

  const hasEscalationFactors = escalationFactors && escalationFactors.length > 0;
  const _rowHeightFactor = showControlsInSingleRow ? 1 : 2; // factor to multiply the escalation extra height by, to accommodate two rows of controls
  const _rowHeight = hasEscalationFactors ? rowHeight + _rowHeightFactor * escalationExtraHeight : rowHeight;
  const _nodeStartY = hasEscalationFactors ? nodeStartY + escalationExtraHeight : nodeStartY;

  // calculate container sizes based on data
  const { controlContainerWidth, controlContainerHeight } = calculateControlContainerSize(
    causes,
    consequences,
    escalationFactors,
    showControlsInSingleRow
  );

  // container positions
  const {
    causesContainerNodePosition,
    preventativeControlsContainerPosition,
    consequencesContainerNodePosition,
    mitigatingControlsContainerPosition,
  } = resolveContainerPositions(diagramMode, controlContainerWidth);

  /*** NODES ***/
  // Causes
  const causesContainerNode = {
    id: causesContainerId,
    type: 'causes-container',
    position: causesContainerNodePosition,
    data: {}, // data is hardcoded in the causes container node
    style: {
      width: `${baseContainerWidth}px`,
      height: `${controlContainerHeight}px`,
    },
  };

  const causesNodes = createCauseOrConsequenceNodes(
    causes,
    causesContainerId,
    'cause-node',
    'Enter Cause',
    _nodeStartY,
    _rowHeight
  );

  // Preventative Controls (position can be mutated if escalation factors exist)
  const preventativeControlsContainer = {
    id: preventativeControlsContainerId,
    type: 'preventative-controls-container',
    position: preventativeControlsContainerPosition,
    data: {}, // data is hardcoded in the preventative controls container node
    style: {
      width: `${controlContainerWidth}px`,
      height: `${controlContainerHeight}px`,
    },
  };

  const preventativeControlNodes = createControlNodes(
    causes,
    'preventative',
    controlContainerWidth,
    escalationFactors,
    showControlsInSingleRow,
    _nodeStartY,
    _rowHeight
  );
  // MUE (risk scenario)
  const mueNode = {
    id: mue.id,
    type: 'mue-node',
    position: {
      x: containerStartX + baseContainerWidth + controlContainerWidth + containerSpacingX + containerToMueNodeSpacingX,
      y: controlContainerHeight / 2 - mueNodeHeight / 2,
    },
    data: {
      recordId: mue.recordId,
      linkUrl: mue.linkUrl,
      label: mue.label,
      editMode: Boolean(mue.editMode),
      placeholder: 'Enter Risk Scenario',
    },
  };

  // Hazard
  const hazardNode = hazard && {
    id: hazard.id,
    type: 'hazard-node',
    position: {
      x: mueNode.position.x + (mueNodeWidth - nodeWidth) / 2,
      y: 0,
    },
    data: {
      recordId: mue.recordId,
      label: hazard.label,
      editMode: Boolean(hazard.editMode),
      placeholder: 'Enter Hazard',
    },
  };

  // mitigating controls (position can be mutated if escalation factors exist)
  const mitigatingControlsContainer = {
    id: mitigatingControlsContainerId,
    type: 'mitigating-controls-container',
    position: mitigatingControlsContainerPosition,
    data: {}, // data is hardcoded in the mitigating controls container node
    style: {
      width: `${controlContainerWidth}px`,
      height: `${controlContainerHeight}px`,
    },
  };

  const mitigatingControlNodes = createControlNodes(
    consequences,
    'mitigating',
    controlContainerWidth,
    escalationFactors,
    showControlsInSingleRow,
    _nodeStartY,
    _rowHeight
  );

  // consequences
  const consequencesContainerNode = {
    id: consequencesContainerId,
    type: 'consequences-container',
    position: consequencesContainerNodePosition,
    data: {}, // data is hardcoded in the consequences container node
    style: {
      width: `${baseContainerWidth}px`,
      height: `${controlContainerHeight}px`,
    },
  };

  const consequencesNodes = createCauseOrConsequenceNodes(
    consequences,
    consequencesContainerId,
    'consequence-node',
    'Enter Consequence',
    _nodeStartY,
    _rowHeight
  );

  // Process Escalation Factors
  // These are additional nodes in the control containers, positioned relative to their parent control

  const controlNodeList = [...preventativeControlNodes.flat(), ...mitigatingControlNodes.flat()];
  const controlNodeLookup = createControlNodeLookup(controlNodeList);

  const escalationFactorBarrierNodes = createEscalationFactorBarrierNodes(
    escalationFactors,
    controlNodeLookup,
    showControlsInSingleRow
  );
  const escalationFactorNodes = createEscalationFactorNodes(
    escalationFactors,
    controlNodeLookup,
    showControlsInSingleRow
  );

  /*** NODES END ***/

  /*** EDGES ***/
  const hazardMueEdge = hazard && createEdge(hazard.id, mue.id, 'bottom', 'top', 'straight', { diagramMode });

  const causesEdges = createBasePathEdges(
    causesNodes,
    preventativeControlNodes,
    'right',
    'left',
    EdgeDirection.LeftToRight,
    preventativeControlsContainerId,
    mue.id,
    diagramMode
  );

  const consequencesEdges = createBasePathEdges(
    consequencesNodes,
    mitigatingControlNodes,
    'left',
    'right',
    EdgeDirection.RightToLeft,
    mitigatingControlsContainerId,
    mue.id,
    diagramMode
  );

  const escalationFactorEdges = createEscalationFactorEdges(
    escalationFactorNodes,
    escalationFactorBarrierNodes,
    controlNodeLookup,
    showControlsInSingleRow
  );

  /*** EDGES END ***/

  // return diagram config of nodes and edges
  const nodes: Array<Node> = [
    // Causes side
    causesContainerNode,
    ...causesNodes,

    // Preventative controls
    preventativeControlsContainer,
    ...preventativeControlNodes.flat(),

    // MUE (center)
    mueNode,

    // Mitigating controls
    mitigatingControlsContainer,
    ...mitigatingControlNodes.flat(),

    // Consequences side
    consequencesContainerNode,
    ...consequencesNodes,

    // EF and EFB nodes (positioned absolutely below controls)
    ...escalationFactorBarrierNodes,
    ...escalationFactorNodes,
  ];

  // add hazard node if hazard exists
  hazardNode && nodes.push(hazardNode);

  const edges: Array<Edge> = [...causesEdges, ...consequencesEdges, ...escalationFactorEdges];

  // add hazard-mue edge if hazard exists
  hazardMueEdge && edges.push(hazardMueEdge);

  return { nodes, edges };
};

/**
 * Creates an edge object representing a connection between two nodes in a flow graph.
 *
 * @param source - The ID of the source node where the edge starts
 * @param target - The ID of the target node where the edge ends
 * @param sourceHandle - The handle identifier on the source node where the edge starts from
 * @param targetHandle - The handle identifier on the target node where the edge connects
 * @param type - The type of the edge
 * @param data - Optional additional data to be associated with the edge
 * @returns An edge object with generated ID and specified properties
 *
 * @example
 * ```typescript
 * const edge = createEdge('node1', 'node2', 'output1', 'input1', 'default', { label: 'connection' });
 * ```
 */
const createEdge = (
  source: string,
  target: string,
  sourceHandle: string,
  targetHandle: string,
  type: string,
  data?: Record<string, unknown>
) => {
  return {
    id: `${source}-${target}-${sourceHandle}-${targetHandle}`,
    source,
    target,
    sourceHandle,
    targetHandle,
    type,
    data,
  };
};

const isEscalationFactorLinkedToControl = (
  escalationFactor: EscalationFactorDiagramNode,
  control: { id: string; recordId?: number }
) => {
  if (escalationFactor.controlNodeId) {
    return control.id === escalationFactor.controlNodeId;
  }

  return control.recordId === escalationFactor.controlRecordId;
};

const calculateMaxBarrierNodesInRows = (rows: ControlRows, escalationFactors: Array<EscalationFactorDiagramNode>) => {
  if (escalationFactors.length === 0) {
    return 0;
  }

  return Math.max(
    ...rows.map((row) => {
      const linkedEscalationFactors = escalationFactors.filter((escalationFactor) =>
        row.controls.some((control) => isEscalationFactorLinkedToControl(escalationFactor, control))
      );

      return linkedEscalationFactors.reduce(
        (sum, escalationFactor) => sum + (escalationFactor.barriers?.length ?? 0),
        0
      );
    }),
    0
  );
};

/**
 * Calculates the dimensions of a control container based on the number of causes and consequences.
 *
 * @param causes - An array of cause diagram nodes, each containing a list of controls
 * @param consequences - An array of consequence diagram nodes, each containing a list of controls
 *
 * @returns An object containing the calculated width and height of the control container
 *   - controlContainerWidth: The width calculated based on the maximum number of controls in a single line
 *   - controlContainerHeight: The height calculated based on the maximum number of rows needed
 */
const calculateControlContainerSize = (
  causes: Array<CauseDiagramNode>,
  consequences: Array<ConsequenceDiagramNode>,
  escalationFactors: Array<EscalationFactorDiagramNode>,
  showControlsInSingleRow?: boolean
) => {
  // calculate container sizes based on data
  const maxPreventativeControlsInOneRow = Math.max(...causes.map((cause) => cause.controls.length), 1);
  const maxMitigatingControlsInOneRow = Math.max(...consequences.map((consequence) => consequence.controls.length), 1);

  const divisor = showControlsInSingleRow ? 1 : 2; // divisor to determine whether we show controls in a single row or two rows

  const maxControlsInOneRowSection = Math.ceil(
    Math.max(maxPreventativeControlsInOneRow, maxMitigatingControlsInOneRow) / divisor
  );

  let controlContainerWidth = baseContainerWidth;
  if (maxControlsInOneRowSection > 1) {
    controlContainerWidth =
      maxControlsInOneRowSection * nodeWidth + (maxControlsInOneRowSection - 1) * cellDeltaX + 2 * nodeStartX;
  }

  // adjust width for the shift of the last node if the number of controls is even
  if (maxPreventativeControlsInOneRow % 2 === 0 || maxMitigatingControlsInOneRow % 2 === 0) {
    controlContainerWidth += nodeShiftDeltaX;
  }

  // Add extra width for escalation factor and barrier nodes that branch out horizontally
  const maxBarrierNodesInOneRowSection = Math.max(
    calculateMaxBarrierNodesInRows(causes, escalationFactors),
    calculateMaxBarrierNodesInRows(consequences, escalationFactors)
  );

  if (maxBarrierNodesInOneRowSection > 0) {
    // NOTE: this could probably be improved
    controlContainerWidth += maxBarrierNodesInOneRowSection * (nodeWidth + cellDeltaX);
  }

  /* HEIGHT */
  const hasEscalationFactors = escalationFactors && escalationFactors.length > 0;
  const _rowHeightFactor = showControlsInSingleRow ? 1 : 2; // factor to multiply the escalation extra height by, to accommodate two rows of controls
  const _rowHeight = hasEscalationFactors ? rowHeight + _rowHeightFactor * escalationExtraHeight : rowHeight;
  const _containerPadding = hasEscalationFactors ? containerPaddingTop + escalationExtraHeight : containerPaddingTop;

  const containerMaxRows = Math.max(causes.length, consequences.length, 1);

  const controlContainerHeight = Math.max(
    baseContainerHeight,
    containerMaxRows * _rowHeight + (containerMaxRows - 1) * rowDeltaY + _containerPadding + containerPaddingBottom
  );

  return { controlContainerWidth, controlContainerHeight };
};

/**
 * Resolves the positions of containers in the bow-tie diagram.
 *
 * @param diagramMode - The mode of the diagram (BOWTIE or BUTTERFLY)
 * @param controlContainerWidth - The calculated width of the control container
 *
 * @returns An object containing position coordinates for different containers:
 *  - causesContainerNodePosition: Position of the causes container
 *  - preventativeControlsContainerPosition: Position of the preventative controls container
 *  - consequencesContainerNodePosition: Position of the consequences container
 *  - mitigatingControlsContainerPosition: Position of the mitigating controls container
 *
 * Each position is represented as an object with x and y coordinates
 */
const resolveContainerPositions = (diagramMode: DIAGRAM_MODE, controlContainerWidth: number) => {
  const causesContainerNodePosition =
    diagramMode === DIAGRAM_MODE.BOWTIE
      ? { x: containerStartX, y: containerStartY }
      : { x: containerStartX + controlContainerWidth + containerSpacingX, y: containerStartY };

  const preventativeControlsContainerPosition =
    diagramMode === DIAGRAM_MODE.BOWTIE
      ? { x: containerStartX + baseContainerWidth + containerSpacingX, y: containerStartY }
      : { x: containerStartX, y: containerStartY };

  const consequencesContainerNodePosition =
    diagramMode === DIAGRAM_MODE.BOWTIE
      ? {
          x:
            containerStartX +
            baseContainerWidth +
            2 * controlContainerWidth +
            2 * containerSpacingX +
            2 * containerToMueNodeSpacingX +
            mueNodeWidth,
          y: containerStartY,
        }
      : {
          x:
            containerStartX +
            baseContainerWidth +
            controlContainerWidth +
            containerSpacingX +
            2 * containerToMueNodeSpacingX +
            mueNodeWidth,
          y: containerStartY,
        };

  const mitigatingControlsContainerPosition =
    diagramMode === DIAGRAM_MODE.BOWTIE
      ? {
          x:
            containerStartX +
            baseContainerWidth +
            controlContainerWidth +
            containerSpacingX +
            2 * containerToMueNodeSpacingX +
            mueNodeWidth,
          y: containerStartY,
        }
      : {
          x:
            containerStartX +
            2 * baseContainerWidth +
            controlContainerWidth +
            2 * containerSpacingX +
            2 * containerToMueNodeSpacingX +
            mueNodeWidth,
          y: containerStartY,
        };

  return {
    causesContainerNodePosition,
    preventativeControlsContainerPosition,
    consequencesContainerNodePosition,
    mitigatingControlsContainerPosition,
  };
};
